import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  closestCorners,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, Typography, Badge, Button, Spin } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { WorkOrder, KanbanColumn } from '../../types';
import WorkOrderCard from './WorkOrderCard';
import './KanbanBoard.css';

const { Text } = Typography;

interface KanbanBoardProps {
  workOrders: WorkOrder[];
  loading?: boolean;
  onWorkOrderUpdate?: (workOrder: WorkOrder) => void;
  onWorkOrderDelete?: (workOrder: WorkOrder) => void;
  onWorkOrderStatusChange?: (workOrderId: number, newStatus: WorkOrder['status']) => void;
  onCreateWorkOrder?: () => void;
  filterStatus?: WorkOrder['status'] | 'all';
}

interface DroppableColumnProps {
  column: KanbanColumn;
  children: React.ReactNode;
}

const DroppableColumn: React.FC<DroppableColumnProps> = ({ column, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: column.id,
    data: {
      type: 'column',
      column,
    },
  });

  const style = {
    transition,
    transform: CSS.Transform.toString(transform),
  };

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        flex: '0 0 auto',
      }}
      className="kanban-column-wrapper"
      {...attributes}
      {...listeners}
    >
      <Card
        className="kanban-column shadow-md border-0"
        styles={{
          body: {
            padding: '20px',
            overflow: 'visible'
          }
        }}
        style={{
          borderTop: `3px solid ${column.color}`,
          background: '#ffffff',
          borderRadius: 10,
        }}
      >
        {/* Column Header */}
        <div
          className="pb-3 mb-4 border-b"
          style={{
            borderColor: `${column.color}30`,
          }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div
                style={{
                  width: 6,
                  height: 24,
                  borderRadius: 3,
                  backgroundColor: column.color,
                  flexShrink: 0,
                }}
              />
              <Text style={{ fontSize: 15, fontWeight: 700, color: '#262626', margin: 0 }}>
                {column.title}
              </Text>
              <Badge
                count={column.workOrders.length}
                style={{
                  backgroundColor: column.color,
                  fontSize: 12,
                  height: 20,
                  lineHeight: '20px',
                  minWidth: 20,
                  padding: '0 7px',
                  fontWeight: 600,
                }}
              />
            </div>
            {column.limit && column.workOrders.length > column.limit && (
              <Badge
                count="!"
                style={{
                  backgroundColor: '#ef4444',
                  fontSize: 12,
                  height: 20,
                  lineHeight: '20px',
                  fontWeight: 700,
                }}
              />
            )}
          </div>
        </div>

        {/* Work Order Cards */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {children}
        </div>

        {/* Empty State */}
        {column.workOrders.length === 0 && (
          <div style={{ marginTop: 48, padding: 24, textAlign: 'center' }}>
            <div
              style={{
                width: 56,
                height: 56,
                margin: '0 auto 16px',
                borderRadius: '50%',
                backgroundColor: `${column.color}15`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  backgroundColor: column.color,
                  opacity: 0.6,
                }}
              />
            </div>
            <p style={{ fontSize: 14, color: '#8c8c8c', fontWeight: 500, margin: 0 }}>
              No {column.title.toLowerCase()} orders
            </p>
            <p style={{ fontSize: 12, color: '#bfbfbf', marginTop: 6 }}>
              Drag work orders here
            </p>
          </div>
        )}
      </Card>
    </div>
  );
};

interface DraggableWorkOrderProps {
  workOrder: WorkOrder;
  onEdit?: (workOrder: WorkOrder) => void;
  onDelete?: (workOrder: WorkOrder) => void;
  onStatusChange?: (workOrder: WorkOrder, newStatus: WorkOrder['status']) => void;
  onCardClick?: (workOrder: WorkOrder) => void;
}

const DraggableWorkOrder: React.FC<DraggableWorkOrderProps> = ({
  workOrder,
  onEdit,
  onDelete,
  onStatusChange,
  onCardClick,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: workOrder.id!.toString(),
    data: {
      type: 'workOrder',
      workOrder,
    },
  });

  const style = {
    transition: isDragging ? 'none' : 'transform 200ms ease',
    transform: CSS.Transform.toString(transform),
    opacity: isDragging ? 0.8 : 1,
    zIndex: isDragging ? 1000 : 'auto',
    scale: isDragging ? '1.05' : '1',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
    >
      <WorkOrderCard
        workOrder={workOrder}
        onEdit={onEdit}
        onDelete={onDelete}
        onStatusChange={onStatusChange}
        onCardClick={onCardClick}
        isDragging={isDragging}
        dragListeners={listeners}
      />
    </div>
  );
};

const KanbanBoard: React.FC<KanbanBoardProps> = ({
  workOrders,
  loading = false,
  onWorkOrderUpdate,
  onWorkOrderDelete,
  onWorkOrderStatusChange,
  onCreateWorkOrder,
  filterStatus = 'all',
}) => {
  const navigate = useNavigate();
  const [activeWorkOrder, setActiveWorkOrder] = useState<WorkOrder | null>(null);

  // Separate sensors for mouse and touch so we can tune them differently:
  // - Mouse: small distance threshold so drag-start feels responsive on desktop
  // - Touch: delay + tolerance so taps and scrolls don't falsely trigger drag
  //   state on mobile (which caused cards to flicker on scroll/tap)
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 8,
      },
    }),
    useSensor(KeyboardSensor)
  );

  const allColumns: KanbanColumn[] = [
    {
      id: 'new',
      title: 'New',
      status: 'new',
      color: '#1890ff',
      workOrders: workOrders.filter(wo => wo.status === 'new' || wo.status === 'created'),
    },
    {
      id: 'scheduled',
      title: 'Scheduled',
      status: 'scheduled',
      color: '#13c2c2',
      workOrders: workOrders.filter(wo => wo.status === 'scheduled'),
    },
    {
      id: 'in_progress',
      title: 'In Progress',
      status: 'in_progress',
      color: '#faad14',
      limit: 5,
      workOrders: workOrders.filter(wo => wo.status === 'in_progress'),
    },
    {
      id: 'waiting_parts',
      title: 'Waiting Parts',
      status: 'waiting_parts',
      color: '#722ed1',
      workOrders: workOrders.filter(wo => wo.status === 'waiting_parts'),
    },
    {
      id: 'waiting_approval',
      title: 'Needs Approval',
      status: 'waiting_approval',
      color: '#fa8c16',
      workOrders: workOrders.filter(wo => wo.status === 'waiting_approval'),
    },
    {
      id: 'completed',
      title: 'Completed',
      status: 'completed',
      color: '#52c41a',
      workOrders: workOrders.filter(wo => wo.status === 'completed'),
    },
  ];

  // Filter columns based on selected status
  const columns = filterStatus === 'all'
    ? allColumns
    : allColumns.filter(col => col.status === filterStatus);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const { active } = event;
    if (active.data.current?.type === 'workOrder') {
      setActiveWorkOrder(active.data.current.workOrder);
    }
  }, []);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;

    setActiveWorkOrder(null);

    if (!over) return;

    const activeId = active.id;
    const overId = over.id;

    // If dropped on the same position, do nothing
    if (activeId === overId) return;

    // Find the work order being dragged
    const activeWorkOrder = workOrders.find(wo => wo.id!.toString() === activeId.toString());
    if (!activeWorkOrder) return;

    // Determine the new status based on drop target
    let newStatus: WorkOrder['status'];

    if (over.data.current?.type === 'column') {
      // Dropped on a column
      newStatus = over.data.current.column.status;
    } else if (over.data.current?.type === 'workOrder') {
      // Dropped on another work order - use that work order's status
      const targetWorkOrder = over.data.current.workOrder;
      newStatus = targetWorkOrder.status;
    } else {
      // Try to find column by ID
      const column = allColumns.find(col => col.id === overId.toString());
      if (column) {
        newStatus = column.status;
      } else {
        return;
      }
    }

    // Only update if status actually changed
    if (activeWorkOrder.status !== newStatus && activeWorkOrder.id) {
      onWorkOrderStatusChange?.(activeWorkOrder.id, newStatus);
    }
  }, [workOrders, allColumns, onWorkOrderStatusChange]);

  const handleWorkOrderEdit = useCallback((workOrder: WorkOrder) => {
    onWorkOrderUpdate?.(workOrder);
  }, [onWorkOrderUpdate]);

  const handleWorkOrderDelete = useCallback((workOrder: WorkOrder) => {
    onWorkOrderDelete?.(workOrder);
  }, [onWorkOrderDelete]);

  const handleStatusChange = useCallback((workOrder: WorkOrder, newStatus: WorkOrder['status']) => {
    if (workOrder.id) {
      onWorkOrderStatusChange?.(workOrder.id, newStatus);
    }
  }, [onWorkOrderStatusChange]);

  const handleCardClick = useCallback((workOrder: WorkOrder) => {
    // Get the current path to determine the role-based route
    const currentPath = window.location.pathname;
    let basePath = '/admin';

    if (currentPath.startsWith('/owner')) {
      basePath = '/owner';
    } else if (currentPath.startsWith('/advisor')) {
      basePath = '/advisor';
    } else if (currentPath.startsWith('/technician')) {
      basePath = '/technician';
    }

    navigate(`${basePath}/work-orders/${workOrder.id}`);
  }, [navigate]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="kanban-board bg-gray-50 pb-8">
      <div style={{ padding: '24px 20px', maxWidth: '1920px', margin: '0 auto' }}>


        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div className="kanban-columns-container">
            {columns.map((column) => (
              <SortableContext
                key={column.id}
                items={column.workOrders.map(wo => wo.id!.toString())}
                strategy={verticalListSortingStrategy}
              >
                <DroppableColumn column={column}>
                  {column.workOrders.map((workOrder) => (
                    <DraggableWorkOrder
                      key={workOrder.id}
                      workOrder={workOrder}
                      onEdit={handleWorkOrderEdit}
                      onDelete={handleWorkOrderDelete}
                      onStatusChange={handleStatusChange}
                      onCardClick={handleCardClick}
                    />
                  ))}
                </DroppableColumn>
              </SortableContext>
            ))}
          </div>

          <DragOverlay>
            {activeWorkOrder ? (
              <WorkOrderCard
                workOrder={activeWorkOrder}
                isDragging={true}
              />
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>
    </div>
  );
};

export default KanbanBoard;